/**
 * Helper functions for DOCX export
 */

import { Paragraph, TextRun, Table } from 'docx';
import type { DocumentSection } from '../types/document-schema';
import { logger } from './logger';

/**
 * Build table of contents entries recursively
 * @param visited - Set of already visited section IDs to prevent infinite loops
 */
export function buildTocEntries(
  section: DocumentSection,
  allSections: DocumentSection[],
  elements: (Paragraph | Table)[],
  level: number,
  visited: Set<string> = new Set()
): void {
  // Safety check
  if (!section) return;
  
  // Prevent circular references
  if (visited.has(section.id)) {
    logger.warn(`[DOCX Export] TOC circular reference detected, skipping: ${section.id}`);
    return;
  }
  visited.add(section.id);
  
  // Limit depth to prevent runaway recursion
  if (level > 10) {
    logger.warn('[DOCX Export] TOC maximum depth exceeded, stopping at level 10');
    return;
  }
  
  // TOC entry
  const indent = '  '.repeat(level);
  const sectionNumber = section.number || '';
  const sectionTitle = section.title || 'Untitled Section';
  
  elements.push(
    new Paragraph({
      children: [
        new TextRun({ text: `${indent}${sectionNumber} ${sectionTitle}` })
      ],
      spacing: { after: 100 }
    })
  );
  
  // Process children (pass visited set)
  if (section.children && Array.isArray(section.children) && section.children.length > 0) {
    for (const childId of section.children) {
      const childSection = allSections.find(s => s && s.id === childId);
      if (childSection) {
        buildTocEntries(childSection, allSections, elements, level + 1, visited);
      }
    }
  }
}
